import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS, SECTION_IDS, scrollToId } from "@/lib/site";
import { Btn } from "@/components/site/Primitives";
import { track } from "@/lib/analytics";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("hero");
  const [menuOpen, setMenuOpen] = useState(false);
  const [hideCta, setHideCta] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active-section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Hide the mobile CTA when the pilot form or footer is substantially in view
  useEffect(() => {
    const targets = ["pilot", "site-footer"].map((id) => document.getElementById(id)).filter(Boolean);
    const inView = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
        setHideCta(inView.size > 0);
      },
      { threshold: 0.2 }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`on-dark fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-natsu-navy/85 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className={`site-container flex items-center justify-between transition-all duration-300 ${scrolled ? "h-14 lg:h-[60px]" : "h-[72px]"}`}>
        {/* Logo — enlarged on desktop; compact mark on mobile after scroll */}
        <button
          onClick={() => go("hero")}
          className="flex items-center transition-opacity hover:opacity-90"
          aria-label="NATSU Intelligence — back to top"
          data-testid="nav-logo"
        >
          <img src="/natsu-logo-nav.png" alt="NATSU Intelligence" className={`hidden w-auto transition-all duration-300 lg:block ${scrolled ? "h-[46px]" : "h-[60px]"}`} draggable="false" />
          <img
            src={scrolled ? "/natsu-mark.png" : "/natsu-logo-nav.png"}
            alt="NATSU Intelligence"
            className={`w-auto lg:hidden ${scrolled ? "h-9" : "h-11"}`}
            draggable="false"
          />
        </button>

        {/* Desktop links */}
        <ul className="hidden items-center gap-7 lg:flex">
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => go(item.id)}
                  data-testid={`nav-link-${item.id}`}
                  className={`relative text-sm font-medium tracking-tight transition-colors duration-200 ${
                    isActive ? "text-natsu-warm-white" : "text-natsu-warm-white/70 hover:text-natsu-warm-white"
                  }`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-natsu-gold transition-all duration-300 ${isActive ? "w-full" : "w-0"}`} />
                </button>
              </li>
            );
          })}
        </ul>

        {/* Desktop CTA */}
        <div className="hidden lg:block">
          <Btn variant="gold" to="pilot" withArrow testId="nav-request-pilot" onClick={() => track("request_pilot_clicked")}>
            Request a Pilot
          </Btn>
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-2.5 lg:hidden">
          {!menuOpen && !hideCta && (
            <button
              onClick={() => { track("request_pilot_clicked"); go("pilot"); }}
              data-testid="nav-request-pilot-mobile"
              className={`inline-flex items-center rounded-full text-[13px] font-semibold tracking-tight transition-all duration-300 ${
                scrolled ? "bg-natsu-gold px-4 py-1.5 text-natsu-navy" : "border border-white/30 px-3.5 py-1.5 text-natsu-warm-white/90"
              }`}
            >
              Pilot
            </button>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-natsu-warm-white transition-colors hover:bg-white/5"
            data-testid="nav-menu-toggle"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu panel */}
      <div
        className={`overflow-hidden border-t bg-natsu-navy/95 backdrop-blur-md transition-[max-height,opacity] duration-300 lg:hidden ${
          menuOpen ? "max-h-[460px] border-white/10 opacity-100" : "max-h-0 border-transparent opacity-0"
        }`}
        data-testid="nav-mobile-menu"
      >
        <ul className="site-container flex flex-col gap-1 py-4">
          {NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => go(item.id)}
                data-testid={`nav-mobile-link-${item.id}`}
                className={`w-full rounded-lg px-3 py-3 text-left text-[15px] font-medium transition-colors ${
                  active === item.id ? "bg-white/5 text-natsu-warm-white" : "text-natsu-warm-white/75 hover:bg-white/5"
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
          <li className="mt-2 px-3">
            <Btn variant="gold" to="pilot" withArrow className="w-full" testId="nav-mobile-request-pilot" onClick={() => track("request_pilot_clicked")}>
              Request a Pilot
            </Btn>
          </li>
        </ul>
      </div>
    </header>
  );
}
